import React, { forwardRef } from 'react'

import styled, { css } from 'styled-components'

import Spinner from 'components/Spinner'

import { color } from 'styles/colors'

type Ref = HTMLButtonElement

type Props = React.PropsWithChildren<{
  showSpinner?: boolean,
  disabled?: boolean,
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'semiDestructive' | 'link',
  type?: 'button' | 'submit',
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void
}>

const LegacyButton = forwardRef<Ref, Props>(({ children, showSpinner = false, variant = 'primary', onClick, disabled = false, type = 'button', ...rest }: Props, ref) => {
  const buttonProps = { variant, ref, disabled, type, onClick, ...rest }

  return (
    <S.Button {...{ ...buttonProps, showSpinner }}>
      { variant === 'link'
        ? <S.Children>{children}</S.Children>
        : (
          <>
            <S.Spinner {...{ showSpinner }} variant={`${variant}Button`} />
            <S.Children {...{ showSpinner }}>{children}</S.Children>
          </>
        )
      }
    </S.Button>
  )
})

export default LegacyButton

const buttonColor = ({ variant, disabled, showSpinner }) => {
  if (disabled && variant === 'primary') {
    return css`
      background-color: ${color('primaryButtonDisabled')};
      border: solid 1px ${color('primaryButtonDisabled')};
      color: ${color('primaryButtonText')};
    `
  } else if (disabled) {
    return css`
    background-color: ${color('disabled')};
    border: solid 1px ${color('disabled')};
  `
  }

  switch (variant) {
    case 'primary': return css`
      background-color: ${showSpinner ? color('primaryButtonProcessing') : color('primaryButton')};
      border: solid 1px ${color('primaryButton')};
      color: ${color('primaryButtonText')};

      &:hover {
        background-color: ${color('primaryButtonHover')};
        border: solid 1px ${color('primaryButtonHover')};
      }

      &:focus {
        outline: solid 1px ${color('primaryButtonFocus')};
      }
    `
    case 'secondary': return css`
      background-color: ${showSpinner ? color('secondaryButtonProcessing') : color('secondaryButton')};
      border: solid 1px ${color('border')};
      color: ${color('secondaryButtonText')};

      &:hover {
        background-color: ${color('secondaryButtonHover')};
        border: solid 1px ${color('secondaryButtonHoverBorder')};
      }
    `
    case 'tertiary': return css`
      background-color: ${showSpinner ? color('tertiaryButtonProcessing') : color('tertiaryButton')};
      border: solid 1px ${color('tertiaryButton')};
      color: ${color('tertiaryButtonText')};

      &:hover {
        background: ${color('tertiaryButtonHover')};
        border: solid 1px ${color('tertiaryButtonHover')};
        color: ${color('tertiaryButtonHoverText')};
      }
    `
    case 'destructive': return css`
      background-color: ${showSpinner ? color('destructiveButtonProcessing') : color('destructiveButton')};
      border: solid 1px ${color('destructiveButton')};
      color: ${color('destructiveButtonText')};

      &:hover {
        background-color: ${color('destructiveButtonHover')};
        border: solid 1px ${color('destructiveButtonHover')};
      }
    `
    case 'semiDestructive': return css`
      border: solid 1px ${color('semiDestructiveButton')};
      color: ${color('semiDestructiveButton')};
      background-color: ${color('semiDestructiveButtonBackground')};

      &:hover {
        background-color: ${color('semiDestructiveButtonHover')};
      }
    `
    default: throw new Error(`Unexpected button variant: ${variant}`)
  }
}

const S = {
  Button: styled.button.withConfig({
    shouldForwardProp: (prop) => !['showSpinner'].includes(prop)
  })<{ variant: string, showSpinner: boolean, disabled: boolean }>`
  ${(props) => {
    const buttonColorString = props.variant !== 'link' ? buttonColor(props) : ''
    const spinnerString = props.showSpinner && 'pointer-events: none;'
    return props.variant === 'link' ? css`
        display: inline;
        color: ${color('anchor')};
        font-weight: 700;
        cursor: pointer;

        &:hover, &:focus {
          color: ${color('anchorHover')};
        }
      ` : css`
        padding: 0.75em 1em;
        text-align: center;
        text-transform: uppercase;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        border-radius: 4px;

        ${buttonColorString}
        ${spinnerString}
    `
  }}
  `,
  Spinner: styled(Spinner).withConfig({
    shouldForwardProp: (prop) => !['showSpinner'].includes(prop)
  })`
    ${({ showSpinner }) => !showSpinner && 'display: none;'}
  `,
  Children: styled.span.withConfig({
    shouldForwardProp: (prop) => !['showSpinner'].includes(prop)
  })<{ showSpinner?: boolean }>`
    ${({ showSpinner }) => showSpinner && css`color: transparent;`}
  `
}
